import React, { useEffect, useRef } from 'react';

// Confirmation modal for destructive actions, in place of window.confirm.
//
// Rendered only while open so the dialog cannot trap focus or intercept Escape
// when it is not on screen. Escape cancels and the backdrop is click-to-cancel,
// matching what the native dialog trained people to expect.
export default function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  busy = false,
  onConfirm,
  onCancel,
}) {
  const dialogRef = useRef(null);
  const confirmRef = useRef(null);
  const returnFocusRef = useRef(null);

  // Focus moves in on open and back to the trigger on close. Kept separate
  // from the key handler below so a `busy` change cannot re-run it and yank
  // focus out of the open dialog.
  useEffect(() => {
    if (!open) return undefined;

    returnFocusRef.current = document.activeElement;
    confirmRef.current?.focus();

    return () => returnFocusRef.current?.focus?.();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        // Ignored while the action is in flight: closing here would imply the
        // request was cancelled when it is still running.
        if (!busy) onCancel?.();
        return;
      }
      if (event.key !== 'Tab') return;

      // Without this, Tab walks out of the dialog and onto the page behind it.
      const focusable = dialogRef.current?.querySelectorAll('button:not([disabled])');
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const confirmClass = tone === 'danger'
    ? 'bg-red-600 hover:bg-red-700 focus-visible:outline-red-600'
    : 'bg-indigo-600 hover:bg-indigo-700 focus-visible:outline-indigo-600';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4"
      onMouseDown={(event) => {
        if (busy) return;
        if (event.target === event.currentTarget) onCancel?.();
      }}
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby={body ? 'confirm-dialog-body' : undefined}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
      >
        <h2 id="confirm-dialog-title" className="text-lg font-semibold text-gray-900">{title}</h2>
        {body && <p id="confirm-dialog-body" className="mt-2 text-sm text-gray-600">{body}</p>}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 cursor-pointer"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition disabled:opacity-50 cursor-pointer ${confirmClass}`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
